import { Decoration, DecorationSet } from '@milkdown/kit/prose/view';
import type { EditorView } from '@milkdown/kit/prose/view';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@milkdown/kit/prose/state';
import { $prose } from '@milkdown/kit/utils';

import { registerUploadPlaceholderBlobUrl } from './uploadProgressDom';

export type UploadPlaceholderKind = 'image' | 'video';

export interface UploadPlaceholderAddPayload {
  id: string;
  pos: number;
  kind: UploadPlaceholderKind;
  file: File;
  width?: number;
  height?: number;
  /** When set, successful upload replaces this document range (e.g. video slash). */
  replaceFrom?: number;
  replaceTo?: number;
}

export type UploadPlaceholderMeta = { clear: true } | { add: UploadPlaceholderAddPayload } | { remove: { id: string } };

export interface UploadPlaceholderState {
  decorations: DecorationSet;
  replaceRanges: Record<string, { from: number; to: number }>;
}

export const uploadPlaceholderDecoKey = new PluginKey<UploadPlaceholderState>('uploadPlaceholderDeco');

const emptyState = (): UploadPlaceholderState => ({
  decorations: DecorationSet.empty,
  replaceRanges: {},
});

function decorationUploadId(deco: Decoration): string | undefined {
  return (deco.spec as { id?: string } | undefined)?.id;
}

function mapReplaceRanges(
  mapping: Transaction['mapping'],
  ranges: Record<string, { from: number; to: number }>,
): Record<string, { from: number; to: number }> {
  const next: Record<string, { from: number; to: number }> = {};
  for (const [id, r] of Object.entries(ranges)) {
    next[id] = { from: mapping.map(r.from), to: mapping.map(r.to) };
  }

  return next;
}

function createRichMediaPlaceholderRoot(
  id: string,
  kind: UploadPlaceholderKind,
  file: File,
  dimensions?: { width?: number; height?: number },
): HTMLElement {
  const root = document.createElement('div');
  root.className = 'bf-media-upload-placeholder';
  root.setAttribute('data-upload-id', id);
  root.setAttribute('role', 'status');
  root.setAttribute('aria-label', 'Uploading');
  root.setAttribute('aria-busy', 'true');

  const blobUrl = URL.createObjectURL(file);
  registerUploadPlaceholderBlobUrl(id, blobUrl);

  const overlay = document.createElement('div');
  overlay.className = 'bf-media-upload-placeholder__overlay';

  const progressOuter = document.createElement('div');
  progressOuter.className = 'bf-circular-progress';
  const inner = document.createElement('div');
  inner.className = 'bf-circular-progress__inner';
  const pct = document.createElement('p');
  pct.className = 'bf-circular-progress__percentage';
  pct.textContent = '0%';
  progressOuter.appendChild(inner);
  progressOuter.appendChild(pct);

  root.appendChild(overlay);
  root.appendChild(progressOuter);

  if (kind === 'image') {
    const img = document.createElement('img');
    img.src = blobUrl;
    img.alt = '';
    img.className = 'bf-media-upload-placeholder__media';
    root.appendChild(img);
  } else {
    const video = document.createElement('video');
    video.src = blobUrl;
    video.autoplay = false;
    video.controls = false;
    video.muted = true;
    video.playsInline = true;
    video.className = 'bf-media-upload-placeholder__media';
    if (dimensions?.width) video.setAttribute('width', String(dimensions.width));
    if (dimensions?.height) video.setAttribute('height', String(dimensions.height));
    root.appendChild(video);
  }

  return root;
}

function removeDecorationByUploadId(set: DecorationSet, doc: EditorState['doc'], id: string): DecorationSet {
  const all = set.find(0, doc.content.size);
  const matches = all.filter((d) => decorationUploadId(d) === id);
  if (!matches.length) return set;

  return set.remove(matches);
}

/** ProseMirror plugin (use in tests via `EditorState.create({ plugins: [...] })`). */
export function createUploadPlaceholderProsePlugin(): Plugin<UploadPlaceholderState> {
  return new Plugin<UploadPlaceholderState>({
    key: uploadPlaceholderDecoKey,
    state: {
      init: () => emptyState(),
      apply: (tr, prev) => {
        let decos = prev.decorations.map(tr.mapping, tr.doc);
        let replaceRanges = mapReplaceRanges(tr.mapping, prev.replaceRanges);

        const meta = tr.getMeta(uploadPlaceholderDecoKey) as UploadPlaceholderMeta | undefined;
        if (meta != null && 'clear' in meta && meta.clear) {
          return emptyState();
        }
        if (meta != null && 'remove' in meta && meta.remove) {
          const rid = meta.remove.id;
          decos = removeDecorationByUploadId(decos, tr.doc, rid);
          replaceRanges = { ...replaceRanges };
          delete replaceRanges[rid];

          return { decorations: decos, replaceRanges };
        }
        if (meta != null && 'add' in meta && meta.add) {
          const p = meta.add;
          const el = createRichMediaPlaceholderRoot(p.id, p.kind, p.file, {
            width: p.width,
            height: p.height,
          });
          const deco = Decoration.widget(p.pos, el, {
            id: p.id,
            side: 1,
            key: `upload-${p.id}`,
          });
          decos = decos.add(tr.doc, [deco]);
          if (p.replaceFrom != null && p.replaceTo != null) {
            replaceRanges = {
              ...replaceRanges,
              [p.id]: { from: p.replaceFrom, to: p.replaceTo },
            };
          }

          return { decorations: decos, replaceRanges };
        }

        return { decorations: decos, replaceRanges };
      },
    },
    props: {
      decorations: (state) => uploadPlaceholderDecoKey.getState(state)?.decorations ?? DecorationSet.empty,
    },
  });
}

export const uploadPlaceholderDecorationPlugin = $prose(() => createUploadPlaceholderProsePlugin());

export function dispatchAddUploadPlaceholder(view: EditorView, payload: UploadPlaceholderAddPayload): void {
  view.dispatch(view.state.tr.setMeta(uploadPlaceholderDecoKey, { add: payload }));
}

export function dispatchRemoveUploadPlaceholder(view: EditorView, id: string): void {
  view.dispatch(view.state.tr.setMeta(uploadPlaceholderDecoKey, { remove: { id } }));
}

export function clearAllUploadPlaceholders(view: EditorView): void {
  view.dispatch(view.state.tr.setMeta(uploadPlaceholderDecoKey, { clear: true }));
}

export function withRemoveUploadPlaceholderById(tr: Transaction, id: string): Transaction {
  return tr.setMeta(uploadPlaceholderDecoKey, { remove: { id } });
}

/** Widget decoration range for point insert (usually from === to). */
export function findUploadPlaceholderWidgetRange(state: EditorState, id: string): [number, number] | null {
  const pluginState = uploadPlaceholderDecoKey.getState(state);
  if (!pluginState) return null;
  const all = pluginState.decorations.find(0, state.doc.content.size);
  const match = all.find((d) => decorationUploadId(d) === id);
  if (!match) return null;

  return [match.from, match.to];
}

/** Document range to replace on success when `replaceFrom`/`replaceTo` were set on add. */
export function getPendingReplaceRangeForId(state: EditorState, id: string): { from: number; to: number } | undefined {
  return uploadPlaceholderDecoKey.getState(state)?.replaceRanges[id];
}
